import { useState, useEffect } from 'react';
import * as xml2js from 'xml2js';
import { FeedData, FeedDataset, FeedResource, ParsedFeedData } from '../types/Feed';

export const useFeedData = (slug: string) => {
  const [data, setData] = useState<ParsedFeedData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFeed = async (investmentSlug: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/feed/${investmentSlug}.xml`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const xmlText = await response.text();
      const parser = new xml2js.Parser({
        explicitArray: false,
        ignoreAttrs: false,
        mergeAttrs: true
      });

      const result = await parser.parseStringPromise(xmlText);
      console.log('Full parsed result:', JSON.stringify(result, null, 2));

      // Handle different XML structures
      const feed = result.rdf?.RDF ||
                  result.RDF ||
                  result['rdf:RDF'] ||
                  result.feed ||
                  result['ns2:datasets'] ||
                  result;

      console.log('Selected feed:', JSON.stringify(feed, null, 2));

      // Check if it's a dane.gov.pl format with multiple datasets
      const rawDatasets = feed?.dataset;
      const hasMultipleDatasets = Array.isArray(rawDatasets);

      const datasets: FeedDataset[] = [];

      if (hasMultipleDatasets) {
        // Multiple datasets case
        console.log('Multiple datasets detected:', rawDatasets.length);

        rawDatasets.forEach((dataset: any) => {
          const resources = parseResources(dataset);

          datasets.push({
            id: dataset?.extIdent || `dataset-${Math.random()}`,
            title: {
              pl: dataset?.title?.polish || 'Brak tytułu',
              en: dataset?.title?.english || 'No title'
            },
            description: {
              pl: dataset?.description?.polish || 'Brak opisu',
              en: dataset?.description?.english || 'No description'
            },
            resources
          });
        });
      } else {
        // Single dataset case (legacy support)
        console.log('Single dataset detected');

        const dataset = rawDatasets || feed;
        const resources = parseResources(dataset, feed);

        datasets.push({
          id: dataset?.extIdent || 'default-dataset',
          title: {
            pl: dataset?.title?.polish || feed?.title || feed?.['dct:title'] || 'Brak tytułu',
            en: dataset?.title?.english || feed?.title || feed?.['dct:title'] || 'No title'
          },
          description: {
            pl: dataset?.description?.polish || feed?.description || feed?.['dct:description'] || 'Brak opisu',
            en: dataset?.description?.english || feed?.description || feed?.['dct:description'] || 'No description'
          },
          resources
        });
      }

      // Group resources by dataset and date
      const availableDates: Record<string, string[]> = {};
      const resourcesByDatasetAndDate: Record<string, Record<string, FeedResource[]>> = {};

      datasets.forEach(dataset => {
        const dateSet = new Set<string>();
        const resourcesByDate: Record<string, FeedResource[]> = {};

        dataset.resources.forEach(resource => {
          const date = resource.date;
          if (!resourcesByDate[date]) {
            resourcesByDate[date] = [];
            dateSet.add(date);
          }
          resourcesByDate[date].push(resource);
        });

        // Sort dates
        const sortedDates = Array.from(dateSet).sort((a, b) =>
          new Date(b).getTime() - new Date(a).getTime()
        );

        availableDates[dataset.id] = sortedDates;
        resourcesByDatasetAndDate[dataset.id] = resourcesByDate;
      });

      const feedData: FeedData = { datasets };

      const parsedData: ParsedFeedData = {
        ...feedData,
        availableDates,
        resourcesByDatasetAndDate
      };

      console.log('Final parsed data:', {
        datasetsCount: datasets.length,
        datasets: datasets.map(d => ({
          id: d.id,
          title: d.title.pl,
          resourcesCount: d.resources.length,
          dates: availableDates[d.id]
        }))
      });

      setData(parsedData);
    } catch (err) {
      console.error('Error fetching feed:', err);
      setError(err instanceof Error ? err.message : 'Błąd podczas pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      fetchFeed(slug);
    }
  }, [slug]);

  return { data, loading, error, refetch: () => fetchFeed(slug) };
};

// Helper function to parse resources from dataset
const parseResources = (dataset: any, feed?: any): FeedResource[] => {
  // Extract resources - check multiple possible structures
  const distributions = feed?.Distribution ||
                       feed?.['dcat:Distribution'] ||
                       feed?.distribution ||
                       dataset?.resources?.resource ||
                       [];

  const resources = Array.isArray(distributions) ? distributions : [distributions].filter(Boolean);

  return resources
    .filter(Boolean)
    .map((resource: any): FeedResource => ({
      id: resource?.identifier ||
          resource?.['dct:identifier'] ||
          resource?.['@_rdf:about'] ||
          resource?.extIdent ||
          Math.random().toString(),
      url: resource?.accessURL ||
           resource?.['dcat:accessURL'] ||
           resource?.downloadURL ||
           resource?.['dcat:downloadURL'] ||
           resource?.url || '',
      title: {
        pl: resource?.title?.polish || resource?.title || resource?.['dct:title'] || 'Brak tytułu',
        en: resource?.title?.english || resource?.title || resource?.['dct:title'] || 'No title'
      },
      date: extractDate(resource?.modified ||
                       resource?.['dct:modified'] ||
                       resource?.created ||
                       resource?.['dct:created'] ||
                       resource?.dataDate ||
                       new Date().toISOString()),
      status: resource?.status || 'active',
      category: resource?.theme || resource?.['dcat:theme'] || 'ECON'
    }));
};

const extractDate = (dateString: string): string => {
  try {
    return new Date(dateString).toISOString().split('T')[0];
  } catch {
    return new Date().toISOString().split('T')[0];
  }
};